/**
 * Template registry — the 6 practitioner site templates.
 *
 * t1_classic is the original design; t2..t6 are the five distinct themes. A template is
 * declared here and nowhere else, and it is exactly two things:
 *
 *   1. TOKENS — default colors, fonts (CDN URLs), preview image. Consumed by
 *      shared/TemplateShell.jsx (CSS variables + font links) and by the admin's site
 *      builder (previews). Pure data.
 *   2. CHROME — the header and footer components that frame every page of the site, plus the
 *      class the page's `<main>` stands on. Registered in `TEMPLATE_CHROME` below.
 *
 * The chrome half exists because it was previously missing: the homepage got its theme's
 * header from the theme's own file while the seven interior pages imported Classic's
 * Navbar/Footer statically, so crossing from a themed homepage to /boutique swapped the
 * whole chrome to Classic (BUG-032) — and no themed header linked to an interior page in the
 * first place (BUG-006). `getTemplateChrome()` is the single resolution point, used by
 * SiteTemplateRenderer (homepage) and ThemedPageLayout (interior pages).
 *
 * TWO INKS, NOT ONE (BUG-065). `colors.text` is the ink for the theme's OWN ground
 * (`colors.background`); `colors.inkOnLight` is the ink for a LIGHT surface the theme does
 * not own — the white/cream cards the interior pages paint their content on. A theme used to
 * declare only `text`, so on the two dark themes every such card inherited a near-white ink
 * and its content became invisible. Both are required of every template; for a theme whose
 * own ground is already light the two are simply the same value, which is what makes the
 * rule TemplateShell emits a no-op there. See shared/TemplateShell.jsx for how it is applied.
 */
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { MINIMAL_CHROME } from "./chrome/MinimalChrome";
import { ORGANIC_CHROME } from "./chrome/OrganicChrome";
import { BOLD_CHROME } from "./chrome/BoldChrome";
import { CLINICAL_CHROME } from "./chrome/ClinicalChrome";
import { LUXE_CHROME } from "./chrome/LuxeChrome";

export const TEMPLATES = [
  {
    id: "t1_classic",
    name: "Classic / Editorial",
    taglineKey: "registry.t1_classic.tagline",
    preview_image: "https://images.unsplash.com/photo-1598555773577-8a7f55242b50?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    colors: { primary: "#2C3531", secondary: "#7FB8A4", accent: "#E6B85C", background: "#F4EFE2", text: "#2C3531", inkOnLight: "#2C3531" },
    fonts: { heading: "Cormorant Garamond", body: "Figtree" },
    fontUrls: [],
  },
  {
    id: "t2_minimal",
    name: "Minimal / Clean",
    taglineKey: "registry.t2_minimal.tagline",
    preview_image: "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    colors: { primary: "#1C1C1C", secondary: "#F3F2EF", accent: "#111111", background: "#FFFFFF", text: "#111111", inkOnLight: "#111111" },
    fonts: { heading: "Outfit", body: "Manrope" },
    fontUrls: ["https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700&display=swap"],
  },
  {
    id: "t3_warm_organic",
    name: "Warm / Organic",
    taglineKey: "registry.t3_warm_organic.tagline",
    preview_image: "https://images.unsplash.com/photo-1699371830139-cb02e94878f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    colors: { primary: "#3E2A21", secondary: "#E5D9C5", accent: "#C97A7E", background: "#FDFBF7", text: "#2B201D", inkOnLight: "#2B201D" },
    fonts: { heading: "Fraunces", body: "Nunito Sans" },
    fontUrls: ["https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;1,9..144,500&family=Nunito+Sans:wght@400;600;700&family=Caveat:wght@600&display=swap"],
  },
  {
    id: "t4_bold_modern",
    name: "Bold / Modern",
    taglineKey: "registry.t4_bold_modern.tagline",
    preview_image: "https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    colors: { primary: "#CCFF00", secondary: "#1A1A1A", accent: "#CCFF00", background: "#0A0A0A", text: "#F3F4F6", inkOnLight: "#1A1A1A" },
    fonts: { heading: "Space Grotesk", body: "IBM Plex Sans" },
    fontUrls: ["https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap"],
  },
  {
    id: "t5_clinical",
    name: "Clinical / Medical",
    taglineKey: "registry.t5_clinical.tagline",
    preview_image: "https://images.unsplash.com/photo-1551076805-e1869033e561?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    colors: { primary: "#2C3E50", secondary: "#EBF5FB", accent: "#3498DB", background: "#FFFFFF", text: "#334155", inkOnLight: "#334155" },
    fonts: { heading: "Work Sans", body: "Work Sans" },
    fontUrls: ["https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700;800&display=swap"],
  },
  {
    id: "t6_luxe",
    name: "Luxe / Elegant",
    taglineKey: "registry.t6_luxe.tagline",
    preview_image: "https://images.unsplash.com/photo-1720118509152-2df877673bee?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    colors: { primary: "#D4AF37", secondary: "#1E293B", accent: "#D4AF37", background: "#0F172A", text: "#F8FAFC", inkOnLight: "#1E293B" },
    fonts: { heading: "Cormorant Garamond", body: "Lora" },
    fontUrls: ["https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&family=Lora:wght@400;500;600&family=Montserrat:wght@400;500;600&display=swap"],
  },
];

export const DEFAULT_TEMPLATE_ID = "t1_classic";

export const SECTION_KEYS = ["story", "approach", "services", "testimonials", "blog", "contact"];

export const getTemplate = (id) => TEMPLATES.find((t) => t.id === id) || TEMPLATES[0];

export const isClassic = (id) => !id || id === "t1_classic";

/**
 * Classic's chrome. Unlike the five themes it takes no CSS variables: its palette and type
 * are Tailwind component classes (index.css, the `eh-*` scale), which is why the layout does
 * NOT wrap it in a TemplateShell.
 */
const CLASSIC_CHROME = {
  Header: Navbar,
  Footer,
  mainClass: "bg-eh-bg text-eh-ink min-h-screen",
};

/**
 * templateId -> { Header, Footer, mainClass }.
 *
 * `Header` and `Footer` both take a single `toggles` prop (`tenant.section_toggles`) and read
 * everything else from the contexts, so the same component instance works on the homepage and
 * on an interior page with no extra wiring.
 */
export const TEMPLATE_CHROME = {
  t1_classic: CLASSIC_CHROME,
  t2_minimal: MINIMAL_CHROME,
  t3_warm_organic: ORGANIC_CHROME,
  t4_bold_modern: BOLD_CHROME,
  t5_clinical: CLINICAL_CHROME,
  t6_luxe: LUXE_CHROME,
};

/**
 * The chrome of a template id. An unknown id falls back to Classic — the same rule
 * `getTemplate` applies to the tokens, so a tenant row carrying a template that no longer
 * exists renders a complete site rather than a page with no header.
 */
export const getTemplateChrome = (id) => TEMPLATE_CHROME[id] || CLASSIC_CHROME;
